import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Copy, Check, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LanguageContext";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); }
  catch { return ""; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / 86400000);
  return Number.isFinite(d) ? Math.max(0, d) : null;
};

const URGENT_DAYS = 5;

const PKG_KEYS = {
  pro_30d: "lo.pkgPro", pro_annual: "lo.pkgProYear",
  business_plus_30d: "lo.pkgElite", business_plus_annual: "lo.pkgEliteYear",
};

// A thank-you the shop didn't ask for, so it has to be impossible to miss and impossible to
// misread: what landed, when it runs out, and one tap to where it gets used.
export const LoyaltyOffer = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const [offers, setOffers] = useState([]);
  const [copied, setCopied] = useState("");
  const [paying, setPaying] = useState("");

  useEffect(() => {
    if (user?.role !== "business") return;
    api.get("/my/offers").then((r) => setOffers(r.data || [])).catch(() => {});
  }, [user?.role]);

  if (!offers.length) return null;

  const copy = (code) => {
    navigator.clipboard?.writeText(code).catch(() => {});
    setCopied(code);
    setTimeout(() => setCopied(""), 2000);
  };

  const pay = async (o) => {
    setPaying(o.id);
    try {
      const { data } = await api.post(`/my/offers/${o.id}/claim`);
      if (data.checkout_url) window.location.href = data.checkout_url;
      else setPaying("");
    } catch (e) {
      setPaying("");
      toast.error(e?.response?.data?.detail || t("lo.payFailed"));
    }
  };

  return (
    <div className="space-y-3" data-testid="loyalty-offers">
      {offers.map((o) => {
        if (o.payRequired) {
          const unlocks = [
            o.promiseCredits > 0 ? t("lo.credits", { n: o.promiseCredits }) : null,
            o.promiseDiscountType
              ? `${o.promiseDiscountType === "percent" ? `${o.promiseDiscountValue}%` : `US$${Number(o.promiseDiscountValue).toFixed(2)}`} ${t("lo.pkgPlan")}`
              : null,
          ].filter(Boolean).join(" · ");
          return (
            <div key={o.id} data-testid={`loyalty-offer-${o.id}`}
              className="rounded-2xl border border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f] p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="h-12 w-12 rounded-xl bg-[#16a34a] text-white flex items-center justify-center shrink-0"><Gift size={22} /></span>
              <div className="flex-1 min-w-0">
                <h3 className="font-extrabold text-foreground" data-testid={`loyalty-title-${o.id}`}>
                  {o.packageName || t("lo.pendingTitle")}
                </h3>
                {o.blurb && <p className="text-sm text-foreground mt-0.5">{o.blurb}</p>}
                {(o.perks || []).length > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">{(o.perks || []).join(" · ")}</p>
                )}
                {unlocks && (
                  <p className="text-xs text-muted-foreground mt-0.5" data-testid={`loyalty-unlocks-${o.id}`}>
                    {t("lo.pendingWhat", { what: unlocks })}
                  </p>
                )}
              </div>
              {o.invoiceOnly ? (
                <p className="shrink-0 max-w-[15rem] rounded-xl border border-[#16a34a]/40 bg-card px-4 py-2.5 text-xs font-semibold text-foreground"
                  data-testid={`loyalty-invoice-${o.id}`}>
                  {t("lo.invoiceMonthly", { price: o.priceLine })}
                </p>
              ) : (
                <button onClick={() => pay(o)} disabled={paying === o.id} data-testid={`loyalty-pay-${o.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#16a34a] hover:bg-[#15803d] px-4 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60">
                  {paying === o.id ? t("lo.paying") : t("lo.pay", { price: o.priceLine })} <ArrowRight size={14} />
                </button>
              )}            </div>
          );
        }
        const creditDays = daysLeft(o.creditsExpiresAt);
        const codeDays = daysLeft(o.codeExpiresAt);
        const soon = [creditDays, codeDays].filter((d) => d !== null);
        const urgent = soon.length > 0 && Math.min(...soon) <= URGENT_DAYS;
        return (
        <div key={o.id} data-testid={`loyalty-offer-${o.id}`}
          className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${urgent ? "border-[#b45309]/40 bg-[#fdf6e7] dark:bg-[#33270f]" : "border-[#16a34a]/30 bg-[#e7f6ec] dark:bg-[#16341f]"}`}>
          <span className={`h-12 w-12 rounded-xl text-white flex items-center justify-center shrink-0 ${urgent ? "bg-[#b45309]" : "bg-[#16a34a]"}`}>
            {urgent ? <Clock size={22} /> : <Gift size={22} />}
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-foreground" data-testid={`loyalty-title-${o.id}`}>
              {urgent ? t("lo.titleSoon", { n: Math.min(...soon) }) : t("lo.title")}
            </h3>
            {o.credits > 0 && (
              <p className="text-sm text-foreground mt-0.5" data-testid={`loyalty-credits-${o.id}`}>
                {t("lo.credits", { n: o.credits })}{" "}
                <span className="text-muted-foreground">
                  {o.creditsExpiresAt ? t("lo.useBy", { date: fmtDate(o.creditsExpiresAt) }) : t("lo.neverExpires")}
                </span>
              </p>
            )}
            {o.code && (
              <p className="text-sm text-foreground mt-1" data-testid={`loyalty-code-line-${o.id}`}>
                {t("lo.codeLine", {
                  off: o.discountType === "percent" ? `${o.discountValue}%` : `US$${Number(o.discountValue).toFixed(2)}`,
                  what: (o.packages || []).map((p) => t(PKG_KEYS[p] || "lo.pkgPlan")).join(" / "),
                })}{" "}
                <span className="text-muted-foreground">{t("lo.codeValid", { date: fmtDate(o.codeExpiresAt) })}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {o.code && (
              <button onClick={() => copy(o.code)} data-testid={`loyalty-copy-${o.id}`}
                className="inline-flex items-center gap-1.5 rounded-xl border border-[#16a34a]/40 bg-card px-3 py-2 text-sm font-extrabold tracking-wider text-[#16a34a] transition-colors hover:bg-secondary">
                {copied === o.code ? <Check size={14} /> : <Copy size={14} />} {o.code}
              </button>
            )}
            <Link to="/pricing" data-testid={`loyalty-use-${o.id}`}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold text-white transition-colors ${urgent ? "bg-[#b45309] hover:bg-[#92400e]" : "bg-[#16a34a] hover:bg-[#15803d]"}`}>
              {o.code ? t("lo.useIt") : t("lo.seePlans")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        );
      })}
    </div>
  );
};

export default LoyaltyOffer;
